import React, { useState, useEffect, useMemo } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Card,
  CardContent,
  CardMedia,
  Button,
  Pagination,
  TextField,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  Checkbox,
} from "@mui/material";
import {
  Download,
  Refresh,
  Sort,
  AccessTime,
  SortByAlpha,
  ImageNotSupported,
  Language,
  FaceRetouchingNatural,
  PhotoCamera,
  UploadFile,
  ArrowBack,
} from "@mui/icons-material";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useParams,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { api, ImageFile, PaginationInfo, StyleResponse } from "./request";
import FaceSearchPage from "./FaceSearchPage";
import ImageDetailDialog from "./ImageDetailDialog";
import { printPhoto } from "./printPhoto";

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#2196f3",
    },
    secondary: {
      main: "#f44336",
    },
    background: {
      default: "#f5f7fa",
    },
  },
  typography: {
    fontFamily:
      '"IBM Plex Sans TC", "Noto Sans TC", -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang TC", "Microsoft JhengHei", "Helvetica Neue", Arial, sans-serif',
    h1: { fontFamily: '"Noto Serif TC", "Noto Serif", "Songti TC", Georgia, serif', fontWeight: 600 },
    h2: { fontFamily: '"Noto Serif TC", "Noto Serif", "Songti TC", Georgia, serif', fontWeight: 600 },
    h3: { fontFamily: '"Noto Serif TC", "Noto Serif", "Songti TC", Georgia, serif', fontWeight: 600 },
    h4: { fontFamily: '"Noto Serif TC", "Noto Serif", "Songti TC", Georgia, serif', fontWeight: 600 },
    h5: { fontFamily: '"Noto Serif TC", "Noto Serif", "Songti TC", Georgia, serif', fontWeight: 600 },
    h6: { fontFamily: '"Noto Serif TC", "Noto Serif", "Songti TC", Georgia, serif', fontWeight: 600 },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 10,
          fontWeight: 500,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          boxShadow: "0 1px 4px rgba(15, 40, 80, 0.07)",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
        },
      },
    },
  },
});

type Lang = "zh-TW" | "en";

const I18N = {
  "zh-TW": {
    initializing: "正在初始化...",
    verifyingLink: "正在驗證連結...",
    verifySuccessLoading: "連結驗證成功，正在載入圖片...",
    verifyFailed: "連結驗證失敗：{message}",
    loadingImages: "正在載入圖片...",
    loadedImages: "已載入 {count} 張圖片",
    loadImagesFailed: "載入圖片失敗：{message}",
    noImagesTitle: "目前沒有圖片",
    noImagesHint: "沒有圖片",
    requestFailed: "請求失敗：{message}",
    confirmDelete: "確定刪除 {name} 嗎？",
    deleted: "已刪除 {name}",
    deleteFailed: "刪除失敗",
    deleteFailedWithError: "刪除失敗：{error}",
    printTitle: "列印 - {name}",
    refreshTitle: "重新整理圖片列表",
    sortByTime: "依時間排序（{hint}）",
    sortByName: "依名稱排序（{hint}）",
    sortNewest: "最新在前",
    sortOldest: "最舊在前",
    sortAZ: "A-Z",
    sortZA: "Z-A",
    sortToggle: "點擊切換",
    noImages: "此資料夾中找不到圖片檔案",
    imageCount: "{count} 張圖片",
    sizeLabel: "大小：",
    modifiedLabel: "修改時間：",
    scanDownload: "掃碼下載",
    qrAlt: "下載二維碼",
    download: "下載",
    print: "列印",
    deleting: "正在刪除...",
    delete: "刪除",
    language: "語言",
    faceSearch: "人臉找圖",
    takePhoto: "拍照",
    uploadPhoto: "上傳照片",
    faceSearching: "正在搜索匹配的圖片...",
    faceNoMatch: "未找到匹配的圖片",
    faceMatchCount: "找到 {count} 張匹配圖片",
    faceSearchFailed: "人臉搜索失敗：{message}",
    cancel: "取消",
    searchAgain: "再搜一次",
    cameraNotSupported: "您的瀏覽器不支持相機功能，請使用上傳照片",
    cameraPermissionDenied: "相機權限被拒絕，請在瀏覽器設置中允許相機權限後重試",
    cameraInsecureContext: "此頁面需要使用HTTPS才能打開相機，請切換到上傳照片方式",
    cameraFailed: "相機啟動失敗：{message}",
    fileTooLarge: "圖片大小不能超過10MB，請選擇較小的圖片",
    uploading: "正在上傳...",
    compressing: "正在壓縮圖片...",
    selectAll: "全選",
    deselectAll: "取消全選",
    batchDownload: "批量下載 ({count})",
    batchDownloading: "正在準備下載...",
    goToPage: "跳至",
    pageUnit: "頁",
  },
  en: {
    initializing: "Initializing...",
    verifyingLink: "Verifying link...",
    verifySuccessLoading: "Link verified. Loading images...",
    verifyFailed: "Link verification failed: {message}",
    loadingImages: "Loading images...",
    loadedImages: "Loaded {count} images",
    loadImagesFailed: "Failed to load images: {message}",
    noImagesTitle: "No images yet",
    noImagesHint: "No images",
    requestFailed: "Request failed: {message}",
    confirmDelete: "Delete {name}?",
    deleted: "Deleted {name}",
    deleteFailed: "Delete failed",
    deleteFailedWithError: "Delete failed: {error}",
    printTitle: "Print - {name}",
    refreshTitle: "Refresh image list",
    sortByTime: "Sort by time ({hint})",
    sortByName: "Sort by name ({hint})",
    sortNewest: "Newest first",
    sortOldest: "Oldest first",
    sortAZ: "A-Z",
    sortZA: "Z-A",
    sortToggle: "Click to toggle",
    noImages: "No images found in this folder",
    imageCount: "{count} images",
    sizeLabel: "Size: ",
    modifiedLabel: "Modified: ",
    scanDownload: "Scan to download",
    qrAlt: "QR code for download",
    download: "Download",
    print: "Print",
    deleting: "Deleting...",
    delete: "Delete",
    language: "Language",
    faceSearch: "Face Search",
    takePhoto: "Take Photo",
    uploadPhoto: "Upload Photo",
    faceSearching: "Searching for matching photos...",
    faceNoMatch: "No matching photos found",
    faceMatchCount: "Found {count} matching photos",
    faceSearchFailed: "Face search failed: {message}",
    cancel: "Cancel",
    searchAgain: "Search Again",
    cameraNotSupported: "Your browser does not support camera. Please upload a photo instead.",
    cameraPermissionDenied: "Camera permission denied. Please allow camera access in browser settings and try again.",
    cameraInsecureContext: "Camera requires HTTPS. Please use the upload photo option instead.",
    cameraFailed: "Camera failed to start: {message}",
    fileTooLarge: "Image size cannot exceed 10MB. Please choose a smaller image.",
    uploading: "Uploading...",
    compressing: "Compressing image...",
    selectAll: "Select All",
    deselectAll: "Deselect All",
    batchDownload: "Batch Download ({count})",
    batchDownloading: "Preparing download...",
    goToPage: "Go to",
    pageUnit: "",
  },
} as const;

type I18nKey = keyof typeof I18N.en;

// ── 样式配置缓存：避免 header 高度在接口返回前跳动 ──────────
type StyleConfig = {
  headerHeightXs: number;
  headerHeightMd: number;
  headerWidthXs: number;
  headerWidthMd: number;
  logoHeight: number;
  headerOverlayOpacity: number;
  backgroundOverlayOpacity: number;
};

const DEFAULT_STYLE_CONFIG: StyleConfig = {
  headerHeightXs: 140,
  headerHeightMd: 220,
  headerWidthXs: 750,
  headerWidthMd: 1920,
  logoHeight: 36,
  headerOverlayOpacity: 0.35,
  backgroundOverlayOpacity: 0,
};

type HeaderCache = {
  header_url: string | null;
  header_url_xs: string | null;
  header_url_md: string | null;
};

const readCachedJson = <T,>(key: string): T | null => {
  if (typeof window === "undefined" || !key) return null;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};

const writeCachedJson = (key: string, value: unknown) => {
  if (typeof window === "undefined" || !key) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // 缓存失败静默忽略
  }
};

const getInitialLang = (): Lang => {
  if (typeof window === "undefined") {
    return "zh-TW";
  }
  const saved = window.localStorage.getItem("gallery_lang");
  if (saved === "zh-TW" || saved === "en") {
    return saved;
  }
  const browserLang = (navigator.language || "").toLowerCase();
  if (browserLang.startsWith("zh")) {
    return "zh-TW";
  }
  return "en";
};

const translate = (lang: Lang, key: I18nKey, vars?: Record<string, string | number>) => {
  const template = I18N[lang][key] || I18N.en[key] || key;
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, token) => String(vars[token] ?? ""));
};

// 接口定义已移至 request.ts 文件

function MainApp() {
  const navigate = useNavigate();
  const { link } = useParams<{ link: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const initialLang = useMemo(() => getInitialLang(), []);
  const [lang, setLang] = useState<Lang>(initialLang);

  const [images, setImages] = useState<ImageFile[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    per_page: 20,
    total: 0,
    pages: 1,
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<{
    message: string;
    type: "success" | "error" | "info";
  }>({ message: translate(initialLang, "initializing"), type: "info" });
  const [selectedImage, setSelectedImage] = useState<ImageFile | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sortBy, setSortBy] = useState<"time" | "name">("time");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [headerImageUrl, setHeaderImageUrl] = useState<string | null>(
    () => readCachedJson<HeaderCache>(link ? `gallery_header_${link}` : "")?.header_url || null
  );
  const [headerImageUrlXs, setHeaderImageUrlXs] = useState<string | null>(
    () => readCachedJson<HeaderCache>(link ? `gallery_header_${link}` : "")?.header_url_xs || null
  );
  const [headerImageUrlMd, setHeaderImageUrlMd] = useState<string | null>(
    () => readCachedJson<HeaderCache>(link ? `gallery_header_${link}` : "")?.header_url_md || null
  );
  const [logoImageUrl, setLogoImageUrl] = useState<string | null>(null);
  const [logoSize, setLogoSize] = useState<number | null>(null);
  const [backgroundImageUrl, setBackgroundImageUrl] = useState<string | null>(
    null
  );
  const [styleConfig, setStyleConfig] = useState<StyleConfig>(() => ({
    ...DEFAULT_STYLE_CONFIG,
    ...(readCachedJson<Partial<StyleConfig>>(link ? `gallery_style_${link}` : "") || {}),
  }));
  const [viewportWidth, setViewportWidth] = useState<number>(
    typeof window !== "undefined" ? window.innerWidth : 0
  );
  const [deleting, setDeleting] = useState(false);
  const [pageJump, setPageJump] = useState("");
  const [faceSearching, setFaceSearching] = useState(false);
  const [faceResults, setFaceResults] = useState<ImageFile[]>([]);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [faceSearchError, setFaceSearchError] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedFaceImage, setSelectedFaceImage] = useState<ImageFile | null>(null);
  const [showFaceSearchPage, setShowFaceSearchPage] = useState(false);
  const [faceEnabled, setFaceEnabled] = useState(true);
  const [selectedFaceIds, setSelectedFaceIds] = useState<Set<number>>(new Set());
  const [batchDownloading, setBatchDownloading] = useState(false);
  const t = (key: I18nKey, vars?: Record<string, string | number>) =>
    translate(lang, key, vars);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang === "zh-TW" ? "zh-Hant" : "en";
    }
    if (typeof window !== "undefined") {
      window.localStorage.setItem("gallery_lang", lang);
    }
    setStatus((prev) => ({
      ...prev,
      message:
        prev.message === I18N["zh-TW"].initializing ||
        prev.message === I18N.en.initializing
          ? translate(lang, "initializing")
          : prev.message,
    }));
  }, [lang]);

  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };
    if (typeof window !== "undefined") {
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
    return undefined;
  }, []);

  // 验证link并加载数据
  useEffect(() => {
    if (!link) return;

    let isMounted = true;

    const verifyAndLoad = async (retryCount = 0) => {
      if (!isMounted) return;

      setStatus({ message: t("verifyingLink"), type: "info" });
      try {
        const data = await api.verifyLink(link);

        if (!isMounted) return;

        if (data.success) {
          setFaceEnabled(data.face_enabled !== false);
          setStatus({
            message: t("verifySuccessLoading"),
            type: "success",
          });
          loadImages("", 1);
        } else {
          setStatus({
            message: t("verifyFailed", { message: data.message || "" }),
            type: "error",
          });
        }
      } catch (error) {
        if (!isMounted) return;

        // 自动重试一次（防止网络抖动）
        if (retryCount < 1) {
          console.log("Link verification failed, retrying...");
          setTimeout(() => verifyAndLoad(retryCount + 1), 500);
          return;
        }

        setStatus({
          message: t("verifyFailed", { message: String(error) }),
          type: "error",
        });
      }
    };

    // 添加小延迟，确保组件完全挂载
    const timer = setTimeout(() => {
      verifyAndLoad();
      loadStyle();
      loadHeaderImage();
      loadLogoImage();
      loadBackgroundImage();
    }, 100);

    // 清理函数：防止内存泄漏和重复请求
    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [link]);

  const loadHeaderImage = async () => {
    if (!link) return;

    try {
      const data = await api.getHeaderImage(link);
      if (data.success) {
        setHeaderImageUrl(data.header_url || null);
        setHeaderImageUrlXs(data.header_url_xs || null);
        setHeaderImageUrlMd(data.header_url_md || null);
        writeCachedJson(`gallery_header_${link}`, {
          header_url: data.header_url || null,
          header_url_xs: data.header_url_xs || null,
          header_url_md: data.header_url_md || null,
        });
      }
    } catch (error) {
      // 静默失败，不影响主功能
      console.error("Failed to load header image:", error);
    }
  };

  const loadBackgroundImage = async () => {
    if (!link) return;

    try {
      const data = await api.getBackgroundImage(link);
      if (data.success && data.background_url) {
        setBackgroundImageUrl(data.background_url);
      }
    } catch (error) {
      // 静默失败，不影响主功能
      console.error("Failed to load background image:", error);
    }
  };

  const loadStyle = async () => {
    if (!link) return;
    try {
      const res: StyleResponse = await api.getStyle(link);
      if (res && res.success) {
        const next: StyleConfig = {
          headerHeightXs: Number(res.header_height_xs || 140),
          headerHeightMd: Number(res.header_height_md || 220),
          headerWidthXs: Number(res.header_width_xs || 750),
          headerWidthMd: Number(res.header_width_md || 1920),
          logoHeight: Number(res.logo_height || 36),
          headerOverlayOpacity: Number(res.header_overlay_opacity ?? 0.35),
          backgroundOverlayOpacity: Number(res.background_overlay_opacity ?? 0),
        };
        setStyleConfig(next);
        writeCachedJson(`gallery_style_${link}`, next);
      }
    } catch (e) {
      // keep defaults on failure
    }
  };

  const loadLogoImage = async () => {
    if (!link) return;

    try {
      const data = await api.getLogoImage(link);
      if (data.success && data.logo_url) {
        setLogoImageUrl(data.logo_url);
        setLogoSize(data.logo_size ?? null);
      } else {
        setLogoImageUrl(null);
        setLogoSize(data.logo_size ?? null);
      }
    } catch (error) {
      // 静默失败，不影响主功能
      console.error("Failed to load logo image:", error);
    }
  };

  const loadImages = async (
    folderPath: string = "",
    page: number = 1,
    currentSortBy?: "time" | "name",
    currentSortOrder?: "asc" | "desc"
  ) => {
    if (!link) return;

    setLoading(true);
    setStatus({ message: "", type: "info" });

    const sortByParam = currentSortBy || sortBy;
    const sortOrderParam = currentSortOrder || sortOrder;

    try {
      const data = await api.getImages(
        link,
        folderPath,
        page,
        20,
        sortByParam,
        sortOrderParam
      );

      if (data.success) {
        setImages(data.images);
        setPagination(data.pagination);
        setCurrentPage(page);
        setStatus({ message: "", type: "success" });
      } else {
        setStatus({
          message: t("loadImagesFailed", { message: data.message || "" }),
          type: "error",
        });
      }
    } catch (error) {
      setStatus({ message: t("requestFailed", { message: String(error) }), type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    loadImages("", page);
  };

  const handleImageClick = (image: ImageFile) => {
    setSelectedImage(image);
    setDialogOpen(true);
  };

  const handleDownload = (imageUrl: string) => {
    if (!imageUrl) return;
    // 直接打开腾讯云URL进行下载
    window.open(imageUrl, "_blank");
  };

  const handlePrint = (imageUrl: string, imageName: string) => {
    printPhoto(imageUrl, t("printTitle", { name: imageName }));
  };

  const detailLabels = {
    sizeLabel: t("sizeLabel"),
    modifiedLabel: t("modifiedLabel"),
    scanDownload: t("scanDownload"),
    qrAlt: t("qrAlt"),
    download: t("download"),
    print: t("print"),
    delete: t("delete"),
    deleting: t("deleting"),
  };

  const handleJumpToPage = () => {
    const n = parseInt(pageJump, 10);
    if (!Number.isFinite(n) || pagination.pages < 1) return;
    const page = Math.min(pagination.pages, Math.max(1, n));
    loadImages("", page);
    setPageJump("");
  };

  const handleDeleteImage = async () => {
    if (!link || !selectedImage) return;
    if (!window.confirm(t("confirmDelete", { name: selectedImage.name }))) {
      return;
    }
    setDeleting(true);
    try {
      const result = await api.deleteImage(link, selectedImage.path);
      if (result.success) {
        setStatus({ message: t("deleted", { name: selectedImage.name }), type: "success" });
        setDialogOpen(false);
        loadImages("", currentPage);
      } else {
        setStatus({ message: result.message || t("deleteFailed"), type: "error" });
      }
    } catch (error) {
      setStatus({ message: t("deleteFailedWithError", { error: String(error) }), type: "error" });
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteFaceImage = async () => {
    if (!link || !selectedFaceImage) return;
    if (!window.confirm(t("confirmDelete", { name: selectedFaceImage.name }))) {
      return;
    }
    setDeleting(true);
    try {
      const result = await api.deleteImage(link, selectedFaceImage.path);
      if (result.success) {
        setStatus({ message: t("deleted", { name: selectedFaceImage.name }), type: "success" });
        setFaceResults((prev) => prev.filter((img) => img.path !== selectedFaceImage.path));
        setSelectedFaceImage(null);
      } else {
        setStatus({ message: result.message || t("deleteFailed"), type: "error" });
      }
    } catch (error) {
      setStatus({ message: t("deleteFailedWithError", { error: String(error) }), type: "error" });
    } finally {
      setDeleting(false);
    }
  };

  const getQRCodeUrl = (imageUrl: string) => {
    if (!imageUrl) return "";
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
      imageUrl
    )}`;
  };

  const handleRefresh = () => {
    loadImages("", currentPage);
  };

  const handleSortChange = (newSortBy: "time" | "name") => {
    if (newSortBy === sortBy) {
      // 如果点击相同的排序方式，切换升序/降序
      const newSortOrder = sortOrder === "desc" ? "asc" : "desc";
      setSortOrder(newSortOrder);
      loadImages("", 1, sortBy, newSortOrder);
    } else {
      // 如果点击不同的排序方式，使用默认排序
      const defaultOrder = newSortBy === "time" ? "desc" : "asc";
      setSortBy(newSortBy);
      setSortOrder(defaultOrder);
      loadImages("", 1, newSortBy, defaultOrder);
    }
  };

  // ══════════════════════════════════════
  //  Face Search
  // ══════════════════════════════════════
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const faceFileInputRef = React.useRef<HTMLInputElement>(null);

  /**
   * Compress image before upload: resize to max 1024px, convert to JPEG.
   * Significantly reduces upload time while keeping enough detail for face recognition.
   */
  const compressImage = (file: File, maxPixels: number = 1024): Promise<File> => {
    return new Promise((resolve, reject) => {
      // If already small, skip compression
      if (file.size < 200 * 1024 && file.type === "image/jpeg") {
        resolve(file);
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          let { width, height } = img;

          // Only resize if larger than maxPixels
          if (width > maxPixels || height > maxPixels) {
            if (width > height) {
              height = Math.round((height / width) * maxPixels);
              width = maxPixels;
            } else {
              width = Math.round((width / height) * maxPixels);
              height = maxPixels;
            }
          }

          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            resolve(file); // fallback to original
            return;
          }
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => {
            if (!blob) {
              resolve(file);
              return;
            }
            const compressed = new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), {
              type: "image/jpeg",
            });
            resolve(compressed);
          }, "image/jpeg", 0.85);
        };
        img.onerror = () => resolve(file); // fallback to original
        img.src = reader.result as string;
      };
      reader.onerror = () => resolve(file);
      reader.readAsDataURL(file);
    });
  };

  // 浏览器返回键支持：从人脸搜图页返回相册
  useEffect(() => {
    const onPopState = (event: PopStateEvent) => {
      const st = event.state;
      if (st && st.view === "face-search") {
        handleStopCamera();
        setSelectedFaceImage(null);
        setShowFaceSearchPage(true);
      } else {
        handleStopCamera();
        setShowFaceSearchPage(false);
        setFaceResults([]);
        setFaceSearching(false);
        setFaceSearchError("");
        setHasSearched(false);
        setSelectedFaceImage(null);
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenFaceSearch = () => {
    if (!faceEnabled) return;
    navigate(`/face-search/${link}`);
  };

  const handleCloseFaceSearch = () => {
    handleStopCamera();
    setShowFaceSearchPage(false);
    setFaceResults([]);
    setFaceSearching(false);
    setFaceSearchError("");
    setHasSearched(false);
    setSelectedFaceImage(null);
    window.history.back();
  };

  const handleStopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
  };

  const handleStartCamera = async () => {
    // Check if mediaDevices API is available (not available in insecure contexts)
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setFaceSearchError(t("cameraNotSupported"));
      return;
    }
    try {
      // Try front camera first, fall back to any camera
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
      });
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err: any) {
      const errName = err?.name || "";
      if (errName === "NotAllowedError" || errName === "PermissionDeniedError") {
        setFaceSearchError(t("cameraPermissionDenied"));
      } else if (errName === "NotFoundError" || errName === "DevicesNotFoundError") {
        setFaceSearchError(t("cameraNotSupported"));
      } else if (errName === "NotReadableError" || errName === "TrackStartError") {
        setFaceSearchError(t("cameraFailed", { message: t("cameraPermissionDenied") }));
      } else if (errName === "OverconstrainedError") {
        // Try again with minimal constraints
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ video: true });
          setCameraStream(stream);
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
          return;
        } catch (fallbackErr: any) {
          setFaceSearchError(t("cameraFailed", { message: String(fallbackErr.message || fallbackErr) }));
        }
      } else {
        setFaceSearchError(t("cameraFailed", { message: String(err.message || err) }));
      }
    }
  };

  const handleCapturePhoto = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    handleStopCamera();
    canvas.toBlob(async (blob) => {
      if (blob) {
        const file = new File([blob], "capture.jpg", { type: "image/jpeg" });
        // Compress captured photo before upload
        setFaceSearching(true);
        setStatus({ message: t("compressing"), type: "info" });
        try {
          const compressed = await compressImage(file, 1024);
          performFaceSearch(compressed, true);
        } catch {
          performFaceSearch(file, true);
        }
      }
    }, "image/jpeg", 0.85);
  };

  const handleFaceFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Check file size limit: 10MB
    if (file.size > 10 * 1024 * 1024) {
      setFaceSearchError(t("fileTooLarge"));
      e.target.value = "";
      return;
    }
    e.target.value = "";
    // Compress before upload to speed up face search
    setFaceSearching(true);
    setStatus({ message: t("compressing"), type: "info" });
    try {
      const compressed = await compressImage(file, 1024);
      performFaceSearch(compressed, true);
    } catch {
      // Fallback: upload original if compression fails
      performFaceSearch(file, true);
    }
  };

  const performFaceSearch = async (file: File, skipLoadingState: boolean = false) => {
    if (!link) return;
    if (!skipLoadingState) {
      setFaceSearching(true);
    }
    setFaceSearchError("");
    setStatus({ message: "", type: "info" });
    setFaceResults([]);
    try {
      const data = await api.faceSearch(link, file);
      if (data.success) {
        setFaceResults(data.images || []);
      } else {
        setFaceSearchError(data.message || t("faceSearchFailed", { message: "" }));
      }
    } catch (err) {
      setFaceSearchError(t("faceSearchFailed", { message: String(err) }));
    } finally {
      setFaceSearching(false);
      setHasSearched(true);
      setSelectedFaceIds(new Set()); // reset selection on new search
    }
  };

  // ── Face Results Selection Helpers ──────────────────────────

  const toggleFaceSelection = (imageId: number) => {
    setSelectedFaceIds(prev => {
      const next = new Set(prev);
      if (next.has(imageId)) {
        next.delete(imageId);
      } else {
        next.add(imageId);
      }
      return next;
    });
  };

  const selectAllFaces = () => {
    const allIds = faceResults.map(img => img.id!).filter(id => id != null);
    setSelectedFaceIds(new Set(allIds));
  };

  const deselectAllFaces = () => {
    setSelectedFaceIds(new Set());
  };

  const handleBatchDownload = async () => {
    if (selectedFaceIds.size === 0 || !link) return;
    setBatchDownloading(true);
    try {
      // 收集选中图片的 path，调用后端 ZIP 批量下载接口
      const selectedImages = faceResults.filter(img => img.id != null && selectedFaceIds.has(img.id));
      const paths = selectedImages.map(img => img.path);
      const blob = await api.batchDownload(link, paths);
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = "images.zip";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 2000);
    } catch (err) {
      console.error("Batch download failed:", err);
    } finally {
      setBatchDownloading(false);
    }
  };

  const resolvedHeaderImageUrl = isMobile
    ? headerImageUrlXs || headerImageUrlMd || headerImageUrl
    : headerImageUrlMd || headerImageUrl || headerImageUrlXs;

  const headerScaleXs = Math.min(
    1,
    Math.max(0.2, viewportWidth / (styleConfig.headerWidthXs || 750))
  );
  const headerScaleMd = Math.min(
    1,
    Math.max(0.2, viewportWidth / (styleConfig.headerWidthMd || 1920))
  );
  const effectiveHeaderHeightXs = Math.max(
    1,
    Math.round((styleConfig.headerHeightXs || 140) * headerScaleXs)
  );
  const effectiveHeaderHeightMd = Math.max(
    1,
    Math.round((styleConfig.headerHeightMd || 220) * headerScaleMd)
  );

  const effectiveLogoHeight = Math.min(
    styleConfig.logoHeight || 36,
    resolvedHeaderImageUrl ? 64 : 40
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          position: "relative",
          minHeight: "100vh",
          ...(backgroundImageUrl
            ? {
                backgroundImage: `url(${backgroundImageUrl})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                backgroundAttachment: "fixed",
              }
            : {
                bgcolor: "#f5f7fa",
              }),
        }}
      >
        {/* Background mask to control "background transparency" (fade the background image) */}
        {backgroundImageUrl && styleConfig.backgroundOverlayOpacity > 0 ? (
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              bgcolor: `rgba(255,255,255,${Math.max(
                0,
                Math.min(0.95, styleConfig.backgroundOverlayOpacity)
              )})`,
              pointerEvents: "none",
              zIndex: 0,
            }}
          />
        ) : null}

        {/* 顶部状态栏区域白条：保持手机状态栏背景为白色 */}
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            height: "env(safe-area-inset-top, 24px)",
            bgcolor: "#ffffff",
            zIndex: 1250,
            pointerEvents: "none",
          }}
        />

        <AppBar
          position="sticky"
          elevation={resolvedHeaderImageUrl ? 0 : 4}
          sx={{
            pt: "env(safe-area-inset-top, 24px)",
            ...(resolvedHeaderImageUrl
              ? {
                  backgroundImage: `url(${resolvedHeaderImageUrl})`,
                  // Stretch to fill the header frame even if the image is smaller.
                  backgroundSize: "100% 100%",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  minHeight: {
                    xs: effectiveHeaderHeightXs,
                    md: effectiveHeaderHeightMd,
                  },
                }
              : {
                  bgcolor: "primary.main",
                }),
          }}
        >
          <Toolbar
            sx={{
              position: "relative",
              minHeight: resolvedHeaderImageUrl
                ? {
                    xs: effectiveHeaderHeightXs,
                    md: effectiveHeaderHeightMd,
                  }
                : 80,
              alignItems: resolvedHeaderImageUrl ? "flex-end" : "center",
              bgcolor: resolvedHeaderImageUrl
                ? `rgba(0,0,0,${Math.max(
                    0,
                    Math.min(0.95, styleConfig.headerOverlayOpacity)
                  )})`
                : "transparent",
              backdropFilter: "none",
              transition: "min-height 0.3s ease",
            }}
          >
            {logoImageUrl && (
              <Box
                component="img"
                src={logoImageUrl}
                alt="Logo"
                sx={{
                  position: "absolute",
                  left: { xs: 12, md: 16 },
                  top: resolvedHeaderImageUrl ? { xs: 8, md: 12 } : "50%",
                  transform: resolvedHeaderImageUrl ? "none" : "translateY(-50%)",
                  height: effectiveLogoHeight,
                  width: "auto",
                  maxWidth: { xs: 160, md: 240 },
                  objectFit: "contain",
                  zIndex: 1,
                }}
              />
            )}
            <Box sx={{ flexGrow: 1 }} />
          </Toolbar>
        </AppBar>

        <Container maxWidth="lg" sx={{ pt: { xs: 2, sm: 3 }, pb: { xs: 2.5, sm: 3 }, px: { xs: 1.5, sm: 2, md: 3 }, position: "relative", zIndex: 1 }}>
          <input
            ref={faceFileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleFaceFileUpload}
          />
          {showFaceSearchPage && (
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Button onClick={handleCloseFaceSearch} startIcon={<ArrowBack />} size="large">
                  {t("faceSearch")}
                </Button>
              </Box>
              <Card>
                <CardContent sx={{ p: 3 }}>
                  {!faceSearching && !hasSearched && !faceSearchError && !selectedFaceImage && (
                    <Box sx={{ textAlign: "center", py: 3 }}>
                      {/* 拍照功能暂时禁用，仅支持上传照片 */}
                      <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
                        <Button variant="contained" startIcon={<UploadFile />} onClick={() => faceFileInputRef.current?.click()} size="large">{t("uploadPhoto")}</Button>
                      </Box>
                    </Box>
                  )}
                  {faceSearching && (
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 6 }}>
                      <CircularProgress size={48} sx={{ mb: 2 }} />
                      <Typography variant="body1">{t("faceSearching")}</Typography>
                    </Box>
                  )}
                  {faceSearchError && !faceSearching && (
                    <Alert severity="error" sx={{ mb: 2 }}>{faceSearchError}</Alert>
                  )}
                  {faceResults.length > 0 && !faceSearching && (
                    <Box>
                      <Alert severity="success" sx={{ mb: 2 }}>{t("faceMatchCount", { count: faceResults.length })}</Alert>
                      {/* Batch select toolbar */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
                        <Button size="small" onClick={selectAllFaces}>{t("selectAll")}</Button>
                        <Button size="small" onClick={deselectAllFaces} disabled={selectedFaceIds.size === 0}>{t("deselectAll")}</Button>
                        <Box sx={{ flexGrow: 1 }} />
                        <Button
                          variant="contained"
                          size="small"
                          color="primary"
                          startIcon={<Download />}
                          disabled={selectedFaceIds.size === 0 || batchDownloading}
                          onClick={handleBatchDownload}
                        >
                          {batchDownloading ? t("batchDownloading") : t("batchDownload", { count: selectedFaceIds.size })}
                        </Button>
                      </Box>
                      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(4, 1fr)" }, gap: { xs: 1.5, sm: 2 } }}>
                        {faceResults.map((image) => {
                          const imgId = image.id!;
                          const isSelected = selectedFaceIds.has(imgId);
                          return (
                          <Card
                            key={image.id || image.path}
                            sx={{
                              cursor: "pointer",
                              overflow: "hidden",
                              transition: "transform 0.2s ease, box-shadow 0.2s ease",
                              outline: isSelected ? "3px solid #2196f3" : "none",
                              outlineOffset: -1,
                              "&:hover": { transform: "translateY(-4px)", boxShadow: "0 8px 24px rgba(15, 40, 80, 0.14)" },
                            }}
                            onClick={(e) => {
                              // Don't open dialog if clicking checkbox
                              if ((e.target as HTMLElement).closest(".face-checkbox")) return;
                              setSelectedFaceImage(image);
                            }}
                          >
                            <Box sx={{ position: "relative", overflow: "hidden" }}>
                              <CardMedia component="img" image={image.thumbnail_url || image.url || ""} alt={image.name} loading="lazy" sx={{ objectFit: "cover", aspectRatio: "1 / 1", width: "100%", bgcolor: "#eef1f5" }} />
                              {image.score != null && (
                                <Box sx={{ position: "absolute", top: 8, right: 8, bgcolor: "rgba(244,67,54,0.85)", color: "white", borderRadius: "10px", px: 1, py: 0.3, fontSize: 11, fontWeight: 600, backdropFilter: "blur(2px)" }}>
                                  {Math.round(image.score)}%
                                </Box>
                              )}
                              <Box className="face-checkbox" sx={{ position: "absolute", top: 4, left: 4 }} onClick={(e) => { e.stopPropagation(); toggleFaceSelection(imgId); }}>
                                <Checkbox
                                  checked={isSelected}
                                  size="small"
                                  sx={{
                                    color: "rgba(255,255,255,0.7)",
                                    "&.Mui-checked": { color: "#2196f3" },
                                    bgcolor: "rgba(0,0,0,0.35)",
                                    borderRadius: "4px",
                                    p: 0.3,
                                  }}
                                />
                              </Box>
                            </Box>
                            <CardContent sx={{ p: 1.25, "&:last-child": { pb: 1.25 } }}>
                              <Typography variant="caption" noWrap title={image.name}>{image.name}</Typography>
                            </CardContent>
                          </Card>
                          );
                        })}
                      </Box>
                      <Box sx={{ mt: 2, textAlign: "center" }}>
                        <Button onClick={() => { setFaceResults([]); setHasSearched(false); }}>{t("searchAgain")}</Button>
                      </Box>
                    </Box>
                  )}
                  {hasSearched && faceResults.length === 0 && !faceSearching && !faceSearchError && (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                      <Typography variant="body1" color="text.secondary">{t("faceNoMatch")}</Typography>
                      <Button sx={{ mt: 2 }} variant="outlined" onClick={() => { setHasSearched(false); setFaceResults([]); }}>{t("searchAgain")}</Button>
                    </Box>
                  )}
                </CardContent>
              </Card>

              <ImageDetailDialog
                open={Boolean(selectedFaceImage)}
                onClose={() => setSelectedFaceImage(null)}
                image={selectedFaceImage}
                isMobile={isMobile}
                labels={detailLabels}
                deleting={deleting}
                scoreLabel={
                  selectedFaceImage?.score != null
                    ? `${t("faceSearch")}: ${Math.round(selectedFaceImage.score)}%`
                    : undefined
                }
                onDownload={() => selectedFaceImage && handleDownload(selectedFaceImage.url)}
                onPrint={() =>
                  selectedFaceImage && handlePrint(selectedFaceImage.url, selectedFaceImage.name)
                }
                onDelete={handleDeleteFaceImage}
                getQRCodeUrl={getQRCodeUrl}
              />
            </Box>
          )}
          <Box sx={{ display: showFaceSearchPage ? "none" : "block" }}>
          {status.message ? (
            <Alert severity={status.type} sx={{ mb: { xs: 2, sm: 3 }, borderRadius: 2.5 }}>
              {status.message}
            </Alert>
          ) : null}

          <Card sx={{ mb: { xs: 2, sm: 3 } }}>
            <CardContent sx={{ p: { xs: 1.25, sm: 2 }, "&:last-child": { pb: { xs: 1.25, sm: 2 } } }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 0.5, sm: 1 },
                  flexWrap: "nowrap",
                  overflow: "hidden",
                }}
              >
                {pagination.total > 0 && (
                  <Chip
                    label={t("imageCount", { count: pagination.total })}
                    color="primary"
                    size={isMobile ? "small" : "medium"}
                    sx={{ flexShrink: 1, minWidth: 0, maxWidth: { xs: 96, sm: "none" } }}
                  />
                )}
                <IconButton
                  onClick={handleRefresh}
                  color="primary"
                  size="small"
                  title={t("refreshTitle")}
                  disabled={loading}
                >
                  <Refresh />
                </IconButton>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                  <IconButton
                    onClick={() => handleSortChange("time")}
                    color={sortBy === "time" ? "primary" : "default"}
                    size="small"
                    title={t("sortByTime", {
                      hint:
                        sortBy === "time"
                          ? sortOrder === "desc"
                            ? t("sortNewest")
                            : t("sortOldest")
                          : t("sortToggle"),
                    })}
                    disabled={loading}
                    sx={{
                      backgroundColor:
                        sortBy === "time" ? "primary.main" : "transparent",
                      color: sortBy === "time" ? "white" : "inherit",
                      "&:hover": {
                        backgroundColor:
                          sortBy === "time"
                            ? "primary.dark"
                            : "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    <AccessTime fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={() => handleSortChange("name")}
                    color={sortBy === "name" ? "primary" : "default"}
                    size="small"
                    title={t("sortByName", {
                      hint:
                        sortBy === "name"
                          ? sortOrder === "desc"
                            ? t("sortZA")
                            : t("sortAZ")
                          : t("sortToggle"),
                    })}
                    disabled={loading}
                    sx={{
                      backgroundColor:
                        sortBy === "name" ? "primary.main" : "transparent",
                      color: sortBy === "name" ? "white" : "inherit",
                      "&:hover": {
                        backgroundColor:
                          sortBy === "name"
                            ? "primary.dark"
                            : "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    <SortByAlpha fontSize="small" />
                  </IconButton>
                </Box>
                {faceEnabled && (
                <Button
                  size="small"
                  onClick={handleOpenFaceSearch}
                  startIcon={<FaceRetouchingNatural />}
                  variant="contained"
                  color="primary"
                  disableElevation
                  sx={{ borderRadius: "20px", textTransform: "none", fontSize: 13, px: { xs: 1.5, sm: 2 }, flexShrink: 0, whiteSpace: "nowrap" }}
                >
                  {t("faceSearch")}
                </Button>
                )}
                <Box sx={{ flexGrow: 1 }} />
                <IconButton
                  size="small"
                  onClick={() => setLang(lang === "zh-TW" ? "en" : "zh-TW")}
                  title={t("language")}
                  aria-label={t("language")}
                  sx={{
                    flexShrink: 0,
                    color: "text.secondary",
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "rgba(0,0,0,0.02)",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.06)" },
                  }}
                >
                  <Language fontSize="small" />
                </IconButton>
              </Box>
            </CardContent>
          </Card>

          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="200px"
            >
              <CircularProgress size={isMobile ? 32 : 40} />
              <Typography variant="body1" sx={{ ml: 2 }} color="text.secondary">
                {t("loadingImages")}
              </Typography>
            </Box>
          ) : images.length === 0 ? (
            <Card
              sx={{
                py: { xs: 5, sm: 6 },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                bgcolor: "#fafbfc",
                border: "1px dashed rgba(0,0,0,0.08)",
              }}
            >
              <Box>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    bgcolor: "rgba(33,150,243,0.12)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 12px",
                  }}
                >
                  <ImageNotSupported sx={{ fontSize: 32, color: "primary.main" }} />
                </Box>
                <Typography variant="h6" sx={{ mb: 0.5, fontSize: { xs: 16, sm: 20 } }}>
                  {t("noImagesTitle")}
                </Typography>
              </Box>
            </Card>
          ) : (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, 1fr)",
                    sm: "repeat(3, 1fr)",
                    md: "repeat(4, 1fr)",
                    lg: "repeat(5, 1fr)",
                  },
                  gap: { xs: 1.5, sm: 2 },
                }}
              >
                {images.map((image) => (
                  <Card
                    key={image.path}
                    sx={{
                      cursor: "pointer",
                      overflow: "hidden",
                      transition: "transform 0.2s ease, box-shadow 0.2s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 8px 24px rgba(15, 40, 80, 0.14)",
                      },
                      "&:active": {
                        transform: "scale(0.97)",
                      },
                    }}
                    onClick={() => handleImageClick(image)}
                  >
                    <CardMedia
                      component="img"
                      image={image.thumbnail_url || ""}
                      alt={image.name}
                      loading="lazy"
                      sx={{
                        objectFit: "cover",
                        aspectRatio: "1 / 1",
                        width: "100%",
                        bgcolor: "#eef1f5",
                      }}
                    />
                    <CardContent sx={{ p: { xs: 1.25, sm: 1.5 }, "&:last-child": { pb: { xs: 1.25, sm: 1.5 } } }}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        noWrap
                        title={image.name}
                        sx={{ fontSize: { xs: 13, sm: 14 } }}
                      >
                        {image.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        display="block"
                        noWrap
                        sx={{ fontSize: { xs: 11, sm: 12 } }}
                      >
                        {image.size_formatted || image.size} • {image.mtime}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>

              {pagination.pages > 0 && (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={1.5}
                  mt={{ xs: 3, sm: 4 }}
                >
                  <Pagination
                    count={pagination.pages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size={isMobile ? "medium" : "large"}
                    siblingCount={isMobile ? 0 : 1}
                    shape="rounded"
                    showFirstButton
                    showLastButton
                  />
                  {pagination.pages > 1 && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                        {t("goToPage")}
                      </Typography>
                      <TextField
                        size="small"
                        type="number"
                        value={pageJump}
                        onChange={(e) => setPageJump(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleJumpToPage();
                        }}
                        inputProps={{
                          min: 1,
                          max: pagination.pages,
                          "aria-label": t("goToPage"),
                        }}
                        sx={{ width: 72, "& input": { textAlign: "center", py: 0.75 } }}
                      />
                      {t("pageUnit") ? (
                        <Typography variant="body2" color="text.secondary">
                          {t("pageUnit")}
                        </Typography>
                      ) : null}
                      <Button size="small" variant="outlined" onClick={handleJumpToPage}>
                        {t("goToPage")}
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
          </Box>
        </Container>
      </Box>

      <ImageDetailDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        image={selectedImage}
        isMobile={isMobile}
        labels={detailLabels}
        deleting={deleting}
        onDownload={() => selectedImage && handleDownload(selectedImage.url)}
        onPrint={() => selectedImage && handlePrint(selectedImage.url, selectedImage.name)}
        onDelete={handleDeleteImage}
        getQRCodeUrl={getQRCodeUrl}
      />
    </ThemeProvider>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/gallery/:link" element={<MainApp />} />
        {/* Backward/alternate path used by admin panel links */}
        <Route path="/display/:link" element={<MainApp />} />
        <Route path="/face-search/:link" element={<FaceSearchPage />} />
        <Route path="/" element={<Navigate to="/gallery" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
